import { motion } from 'framer-motion'
import { Building2, GraduationCap, UserCheck, FileText, TrendingUp } from 'lucide-react'

const UseCases = () => {
  const useCases = [
    {
      icon: Building2,
      title: 'Empresas',
      description: 'Automatize processos internos e melhore a eficiência operacional.',
      color: 'primary',
      examples: [
        'Análise de contratos e documentos legais',
        'Processamento de faturas e recibos',
        'Extração de dados para relatórios',
        'Compliance e auditoria automatizada'
      ],
      benefits: ['Redução de 80% no tempo de processamento', 'Maior precisão na análise', 'Economia de custos operacionais']
    },
    {
      icon: GraduationCap,
      title: 'Educação',
      description: 'Transforme a forma como instituições educacionais gerenciam documentos.',
      color: 'accent',
      examples: [
        'Análise de trabalhos acadêmicos',
        'Processamento de matrículas',
        'Avaliação automática de provas',
        'Gestão de documentos institucionais'
      ],
      benefits: ['Aceleração do processo de avaliação', 'Padronização de critérios', 'Melhor gestão de dados']
    },
    {
      icon: UserCheck,
      title: 'Profissionais Liberais',
      description: 'Potencialize sua produtividade com ferramentas inteligentes.',
      color: 'primary',
      examples: [
        'Análise de documentos jurídicos',
        'Processamento de laudos médicos',
        'Gestão de contratos e acordos',
        'Organização de arquivos pessoais'
      ],
      benefits: ['Foco no trabalho estratégico', 'Organização automática', 'Acesso rápido às informações']
    }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0 }
  }

  return (
    <section className="section-padding bg-gray-50">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Implementações de Sucesso
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Veja como TANDER SMART esta impulsionando a inovação em setores estratégicos e transformando
            o cotidiano de profissionais ao redor do mundo.
          </p>
        </motion.div>

        {/* Use Cases Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid lg:grid-cols-3 gap-8 mb-16"
        >
          {useCases.map((useCase, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ y: -10 }}
              className="card p-8 group"
            >
              {/* Header */}
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-${useCase.color}-100 text-${useCase.color}-500 mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <useCase.icon className="w-8 h-8" />
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {useCase.title}
              </h3>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                {useCase.description}
              </p>

              {/* Examples */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                  <FileText className="w-4 h-4 text-primary-500" />
                  <span>Aplicação</span>
                </h4>
                <ul className="space-y-2">
                  {useCase.examples.map((example, exampleIndex) => (
                    <li key={exampleIndex} className="flex items-start space-x-2 text-sm text-gray-600">
                      <div className={`w-1.5 h-1.5 rounded-full bg-${useCase.color}-400 mt-2 flex-shrink-0`}></div>
                      <span>{example}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Benefits */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4 text-accent-500" />
                  <span>Benefícios</span>
                </h4>
                <ul className="space-y-2">
                  {useCase.benefits.map((benefit, benefitIndex) => (
                    <li key={benefitIndex} className="flex items-start space-x-2 text-sm text-gray-600">
                      <div className={`w-1.5 h-1.5 rounded-full bg-${useCase.color}-400 mt-2 flex-shrink-0`}></div>
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Success Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-2xl p-8 shadow-lg"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Resultados Comprovados
            </h3>
            <p className="text-gray-600">
              Análise os resultados positivos que TANDER SMART pode proporcionar para você e sua organização.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-500 mb-2">80%</div>
              <div className="text-gray-600">Tempo de Processamento</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-accent-500 mb-2">95%</div>
              <div className="text-gray-600">Precisão na Análise</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary-500 mb-2">60%</div>
              <div className="text-gray-600">Economia em Custos Operacionais</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-accent-500 mb-2">24/7</div>
              <div className="text-gray-600">Disponibilidade da Plataforma</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default UseCases
